import {FC, useMemo, useState, useEffect} from 'react';
import {Box, Link, Typography} from '@mui/material';
import NotificationSettingItem from '@/components/Organization/Profile/NotificationSettingItem';
import {BODY2, SUBTITLE1} from '@/utils/constants';
import Modal from '@/shared/components/Modal';
import VIPNotification from '@/components/Organization/Profile/VIPNotification';
import {useSelector} from 'react-redux';
import {
  organizationGetNotificationSettings,
  organizationNotificationSettingLoaderSelector,
  organizationNotificationSettingSelector,
  organizationUpdateNotificationSettings,
} from '@/store/organization-notificationSetting';
import {notificationsSettingsStyle} from './NotificationsSettings.styles';
import {USER_TYPE} from '@/utils/enums/user.enum';
import {orgStorage, userStorage, adminStorage} from '@/utils/services';
import {
  adminNotificationSettingsData,
  organizationNotificationSettingsData,
  userNotificationSettingsData,
} from '@/utils/constants/notificationSettings';
import {
  userGetNotificationSettings,
  userNotificationSettingSelector,
  userUpdateNotificationSettings,
} from '@/store/user-notificationSetting';
import {useAppDispatch} from '@/store/hooks';
import Loader from '@/shared/components/Loader';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {adminNotificationSettingSelector} from '@/store/admin-notificationSettings/selectors';
import {
  adminGetNotificationSettings,
  adminUpdateNotificationSettings,
} from '@/store/admin-notificationSettings/actions';
import {useApiInstance} from '@/contexts/ApiInstanceContext';
import {ROUTER_URL} from '@/utils/enums/router-url.enum';

const SMS_NOTIFICATION_TYPE = 'SMS notifications';

const SmsDisclosure: FC = () => (
  <Box sx={notificationsSettingsStyle.smsDisclosure}>
    <Typography variant={BODY2} sx={notificationsSettingsStyle.smsDisclosureText}>
      By enabling SMS, you agree to receive EventSimpli transactional texts for this event. Msg frequency varies.
      Msg/data rates may apply. Reply STOP to opt out, HELP for help.
    </Typography>
    <Box sx={notificationsSettingsStyle.smsLinks}>
      <Link href="/#" sx={notificationsSettingsStyle.smsLink}>
        SMS Terms
      </Link>
      <Typography variant={BODY2} sx={notificationsSettingsStyle.smsSeparator}>
        |
      </Typography>
      <Link href={ROUTER_URL.ORGANIZATION_PRIVACY_POLICY} sx={notificationsSettingsStyle.smsLink}>
        Privacy Policy
      </Link>
    </Box>
  </Box>
);

const NotificationsSettingsGeneral: FC = () => {
  const isLoading = useSelector(organizationNotificationSettingLoaderSelector);
  const userNotificationSettings = useSelector(userNotificationSettingSelector);
  const organizationNotificationSettings = useSelector(organizationNotificationSettingSelector);
  const adminNotificationSettings = useSelector(adminNotificationSettingSelector);

  const organizationId = orgStorage.get('organizationId') ?? '';
  const userId = userStorage.get('userId') ?? '';
  const adminId = adminStorage.get('superAdminId') ?? '';

  const dispatch = useAppDispatch();

  const [isShowVIPNotificationModal, setIsShowVIPNotificationModal] = useState(false);
  const [isVisiblePhoneButton, setIsVisiblePhoneButton] = useState(false);

  const {role: userType} = useApiInstance();

  const {notificationData, settingsSelector, actionUpdate, id, actionGet} = useMemo(() => {
    switch (userType) {
      case USER_TYPE.USER:
        return {
          notificationData: userNotificationSettingsData,
          settingsSelector: userNotificationSettings,
          actionUpdate: userUpdateNotificationSettings,
          actionGet: userGetNotificationSettings,
          id: userId,
        };
      case USER_TYPE.ORGANIZATION:
        return {
          notificationData: organizationNotificationSettingsData,
          settingsSelector: organizationNotificationSettings,
          actionUpdate: organizationUpdateNotificationSettings,
          actionGet: organizationGetNotificationSettings,
          id: organizationId,
        };
      case USER_TYPE.ADMIN:
        return {
          notificationData: adminNotificationSettingsData,
          settingsSelector: adminNotificationSettings,
          actionUpdate: adminUpdateNotificationSettings,
          actionGet: adminGetNotificationSettings,
          id: adminId,
        };
      default:
        return {
          notificationData: organizationNotificationSettingsData,
          settingsSelector: organizationNotificationSettings,
          actionUpdate: organizationUpdateNotificationSettings,
          actionGet: organizationGetNotificationSettings,
          id: organizationId,
        };
    }
  }, [userType]);

  const [settings, setSettings] = useState(settingsSelector);

  const handleCloseModal = () => {
    setIsShowVIPNotificationModal(false);
  };
  const handlePhoneNotificationChange = () => {
    setIsVisiblePhoneButton(!isVisiblePhoneButton);
  };
  const handleOpenModal = () => {
    setIsShowVIPNotificationModal(true);
  };

  const handleSwitchSettings = async (idValue: string) => {
    const updatedSettings = {
      ...settings,
      [idValue]: !settings?.[idValue],
    };

    setSettings(updatedSettings);

    try {
      await dispatch(actionUpdate({id: id as number, reqBody: updatedSettings})).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    if (settingsSelector) {
      setSettings(settingsSelector);
    }
  }, [settingsSelector]);

  useEffect(() => {
    const getSettings = async () => {
      if (!id) return;

      try {
        const data = await dispatch(actionGet(id as number)).unwrap();
        setSettings(data);
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };

    getSettings();
  }, []);

  return (
    <Box sx={notificationsSettingsStyle.wrapper}>
      {isLoading && !settings ? (
        <Loader />
      ) : (
        <Box>
          {userType === USER_TYPE.ORGANIZATION && (
            <Box>
              <Typography variant={SUBTITLE1}>Phone Notification</Typography>
              <SmsDisclosure />
              <NotificationSettingItem
                title="VIP Notifications"
                description="Receive an SMS message when VIP guests check in. Guests are marked a VIP in the Attendees section."
                isPhoneButton={isVisiblePhoneButton}
                disable={!isVisiblePhoneButton}
                onChange={handlePhoneNotificationChange}
                onClick={handleOpenModal}
              />
            </Box>
          )}

          {settings &&
            notificationData!.map((data) => (
              <Box key={data.notificationType}>
                <Typography variant={SUBTITLE1}>{data.notificationType}</Typography>
                {data.notificationType.trim() === SMS_NOTIFICATION_TYPE && <SmsDisclosure />}
                {data.settings.map((setting) => (
                  <Box key={setting.title}>
                    <NotificationSettingItem
                      isEnable={!!settings?.[setting.value]}
                      title={setting.title}
                      description={setting.description}
                      onChange={() => handleSwitchSettings(setting.value)}
                    />
                  </Box>
                ))}
              </Box>
            ))}

          <Modal
            open={isShowVIPNotificationModal}
            sx={notificationsSettingsStyle.detailsModal}
            onClose={handleCloseModal}
            // closeButtonTitle="Close"
            // isShowSaveButton
            // mainButtonTitle="Confirm"
            // isShowCloseButton
            // isButtonsBottom
            // onSave={handleCloseModal}
          >
            <VIPNotification />
          </Modal>
        </Box>
      )}
    </Box>
  );
};

export default NotificationsSettingsGeneral;
